import React, { useState } from "react";
import { Box } from "@mui/material";
import TextView from "../../TextView";
import Button from "../../Button";
import { ASSETS } from "../../../utils/config";
import { ReactComponent as AppleIcon } from "@assets/icons/mobilePopupIcons/ic_round-apple.svg";
import { ReactComponent as PlayStoreIcon } from "@assets/icons/mobilePopupIcons/playstore.svg";
import { useStoreEmailReferralCode } from "../../../services/mutations";
import {
  StyledModal,
  PopupContainer,
  BenefitsSection,
  BenefitsList,
  EmailInput,
  ErrorText,
  GetAppButton,
  LoginButton,
} from "./styles";

const Popup = ({ open, handleClose }) => {
  const [email, setEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const storeEmailReferralCode = useStoreEmailReferralCode();

  // Utility function to detect iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  const getReferralCode = () => {
    // Check sessionStorage first, then localStorage
    let referralData = sessionStorage.getItem("referralData");
    if (!referralData) {
      referralData = localStorage.getItem("referralData");
    }

    if (referralData) {
      try {
        const parsedData = JSON.parse(referralData);
        return parsedData.referral_code || null;
      } catch (error) {
        console.error("Error parsing referralData:", error);
        return null;
      }
    }
    return null;
  };

  const handleGetAppClick = () => {
    setShowEmailForm(true);
  };

  const handleCancel = () => {
    if (emailSubmitted) {
      setEmailSubmitted(false);
      setEmail("");
      setEmailError("");
    } else if (showEmailForm) {
      setShowEmailForm(false);
      setEmail("");
      setEmailError("");
    } else {
      handleClose();
    }
  };

  const handleEmailSubmit = async () => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }
    if (!emailRegex.test(email)) {
      setEmailError("This email is invalid");
      return;
    }

    setEmailError("");
    setIsSubmitting(true);

    try {
      const referralCode = getReferralCode();

      if (referralCode) {
        // Call the API to store email with referral code
        await storeEmailReferralCode.mutateAsync({
          email: email.trim(),
          referral_code: referralCode,
        });
      }

      // On success, show download button
      setEmailSubmitted(true);
    } catch (error) {
      console.error("Error submitting email:", error);
      const resp = error?.response;

      // If API returns 400 with the specific email message, treat as success
      if (resp && (resp.status === 400 || resp.status_code === 400)) {
        const respStr = JSON.stringify(resp.data || {});
        if (
          respStr.includes(
            "Email Referral Code with this Email Address already exists"
          )
        ) {
          // The email was already stored previously — show download button
          setEmailSubmitted(true);
        } else if (resp?.data?.message) {
          setEmailError(resp.data.message);
        } else {
          setEmailError("Something went wrong. Please try again.");
        }
      } else if (resp?.status === 409) {
        // Duplicate email error mapped to 409 in other flows
        setEmailError("This email already has a referral code stored.");
      } else if (resp?.data?.message) {
        setEmailError(resp.data.message);
      } else {
        setEmailError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadClick = () => {
    const appStoreUrl =
      "https://apps.apple.com/pk/app/iyuga-wheel/id6746421146";
    const playStoreUrl =
      "https://play.google.com/store/apps/details?id=com.iyuga";

    // Directly redirect to stores
    if (isIOS()) {
      window.location.href = appStoreUrl;
    } else {
      window.location.href = playStoreUrl;
    }
  };

  const handleOpenAppClick = () => {
    const iosDeepLink = "iyuga://"; // Custom URL scheme for iOS fallback
    const androidDeepLink =
      "intent://#Intent;scheme=iyuga;package=com.iyuga;end";

    // Track if app was opened
    let appOpened = false;
    let timeoutId;

    // Handler for when app opens (page loses focus)
    const handleBlur = () => {
      appOpened = true;
      clearTimeout(timeoutId);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      handleClose();
    };

    // Handler for visibility change (more reliable on mobile)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        clearTimeout(timeoutId);
        window.removeEventListener("blur", handleBlur);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        handleClose();
      }
    };

    // Set up listeners
    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Try to open the app via universal link
    if (isIOS()) {
      window.location.href = iosDeepLink;
    } else {
      window.location.href = androidDeepLink;
    }

    // Clean up listeners after timeout
    timeoutId = setTimeout(() => {
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, 2500); // 2.5 second timeout
  };

  return (
    <StyledModal open={open} onClose={handleClose}>
      <PopupContainer>
        <img src={ASSETS.PopupLogo} alt="popup-logo" style={{width:"100px",height:"100px"}} />

        <TextView
          variant="title" 
          style={{fontSize:"20px", color: "#123051", fontWeight: 700}}
        >
          {showEmailForm 
            ? emailSubmitted
              ? "Ready to download!"
              : "Providing your email lets us give credit to the owner of this Wheel"
            : "Explore of a World of Wheels on the FREE iYuga app!"}
        </TextView>

        {!showEmailForm && (
          <>
            <TextView
              variant="largeText"
              style={{
                textAlign: "center",
                fontSize: "15px",
                color: "#123051",
              }}
            >
              View & Clone awesome Wheels!
            </TextView>

            <BenefitsSection>
              <BenefitsList>
                <li><TextView variant="largeText" style={{fontSize: "14px"}}>No bait & switch! Our <b>Free</b> version is great!</TextView></li>
                <li><TextView variant="largeText" style={{fontSize: "14px"}}>Wheel Building Wizards can enjoy 50% off!</TextView></li>
              </BenefitsList>
            </BenefitsSection>
          </>
        )}

        {showEmailForm && (
          <>
            {!emailSubmitted ? (
              <>
                <TextView
                  variant="largeText"
                  style={{
                    textAlign: "center",
                    fontSize: "15px",
                    color: "#123051",
                    marginBottom: "16px",
                  }}
                >
                  <b>Don't worry!</b> We won't save your info if you don't create an account within 24 hours.
                </TextView>

                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
                  <Box>
                    <EmailInput
                      placeholder="Enter email you will use for the app"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError(""); // Clear error on typing
                      }}
                      type="email"
                      error={!!emailError}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !isSubmitting) {
                          handleEmailSubmit();
                        }
                      }}
                    />
                    {emailError && (
                      <ErrorText>{emailError}</ErrorText>
                    )}
                  </Box>

                  <Button style={{backgroundColor: "black", color: "white"}} onClick={handleEmailSubmit} isLoading={isSubmitting}>
                    {!isSubmitting ? "Submit"  :""}
                  </Button>

                  <TextView
                    variant="largeText"
                    style={{
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#123051",
                      marginTop: "8px",
                    }}
                  >
                    To continue on this browser:
                  </TextView>

                  <Box sx={{ display: "flex", gap: "12px", width: "100%" }}>
                    <Button variant="outlined" onClick={handleCancel} style={{ flex: 1 }}>
                      Cancel
                    </Button>
                    <LoginButton 
                      onClick={() => {
                        // Navigate to login
                        window.location.href = "/login";
                      }}
                    >
                      Log in
                    </LoginButton>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <TextView
                  variant="largeText"
                  style={{
                    textAlign: "center",
                    fontSize: "15px",
                    color: "#123051",
                    marginBottom: "24px",
                  }}
                >
                  Thank you! Click below to download the app.
                </TextView>

                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
                  <GetAppButton onClick={handleDownloadClick}>
                    {isIOS() ? (
                      <>
                        <AppleIcon style={{ width: 20, height: 20 }} />
                        Download now
                      </>
                    ) : (
                      <>
                        <PlayStoreIcon style={{ width: 20, height: 20 }} />
                        Download now
                      </>
                    )}
                  </GetAppButton>

                  <TextView
                    variant="largeText"
                    style={{
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#123051",
                      marginTop: "8px",
                    }}
                  >
                    To continue on this browser:
                  </TextView>

                  <Box sx={{ display: "flex", gap: "12px", width: "100%" }}>
                    <Button variant="outlined" onClick={handleCancel} style={{ flex: 1 }}>
                      Cancel
                    </Button>
                    <LoginButton 
                      onClick={() => {
                        // Navigate to login
                        window.location.href = "/login";
                      }}
                    >
                      Log in
                    </LoginButton>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}

        {!showEmailForm && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
            <GetAppButton onClick={handleGetAppClick}>
              {isIOS() ? (
                <>
                  <AppleIcon style={{ width: 20, height: 20 }} />
                  Get the App
                </>
              ) : (
                <>
                  <PlayStoreIcon style={{ width: 20, height: 20 }} />
                  Get the App
                </>
              )}
            </GetAppButton>

            <TextView>Already installed the iYuga app?</TextView>

            <Button onClick={handleOpenAppClick}>Open iYuga App</Button>

            <Button variant="outlined" onClick={handleClose}>
              No thanks
            </Button>
          </Box>
        )}
      </PopupContainer>
    </StyledModal>
  );
};

export default Popup;
